import React from 'react';
import { OibDropdownProps } from './types';
import { isOibValid } from './validation';
import { DropdownAutocomplete } from 'ui/components/Dropdown/DropdownAutocomplete';
import { DropdownOption } from 'ui/components/Dropdown/DropdownAutocomplete/types';
import { sudRegLookupAPI } from 'services/api/contacts/api';
import { SudRegResponse } from 'services/api/contacts/types';


const OibDropdown: React.FC<OibDropdownProps> = ({
  value,
  error,
  placeholder = 'Unesite OIB (11 znamenki)',
  name,
  required,
  disabled,
  label,
  onSudRegSelect,
}) => {
  const [options, setOptions] = React.useState<DropdownOption[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [sudRegResult, setSudRegResult] = React.useState<SudRegResponse | null>(null);
  const [searchText, setSearchText] = React.useState('');

  const handleSearchChange = React.useCallback(async (searchValue: string) => {
    const cleaned = searchValue.trim().replace(/\D/g, '');
    setSearchText(cleaned);

    if (cleaned.length !== 11 || !isOibValid(cleaned)) {
      setOptions([]);
      setSudRegResult(null);
      return;
    }

    setIsLoading(true);
    try {
      const result = await sudRegLookupAPI(cleaned);
      setSudRegResult(result);

      if (result.name) {
        setOptions([
          {
            value: cleaned,
            text: (
              <div className="flex items-center justify-between w-full">
                <span className="font-medium">{result.name}</span>
                <span className="text-xs bg-blue-200 text-blue-900 py-0.5 px-2 rounded-sm block ml-2.5">OIB: {cleaned}</span>
              </div>
            ),
          },
        ]);
      } else {
        setOptions([]);
      }
    } catch {
      setOptions([]);
      setSudRegResult(null);
    }
    setIsLoading(false);
  }, []);

  const handleSearchFocus = React.useCallback(() => {}, []);

  const handleDropdownChange = React.useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      if (sudRegResult && onSudRegSelect) {
        onSudRegSelect(item.value, sudRegResult);
      }
    },
    [sudRegResult, onSudRegSelect]
  );

  const digitCount = searchText.length;
  const hasDigits = digitCount > 0;
  const isValid = digitCount === 11 && isOibValid(searchText);

  // Osiguraj da se već odabrani OIB uvijek može prikazati, čak i kad nema
  // aktivne sudreg pretrage (npr. povratak na prethodni korak čardarije).
  const displayOptions = React.useMemo(() => {
    if (value && !options.some((o) => o.value === value)) {
      return [{ value, text: value }, ...options];
    }
    return options;
  }, [value, options]);

  return (
    <div className="relative">
      <DropdownAutocomplete
        name={name || ''}
        label={label}
        value={value}
        error={error}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        options={displayOptions}
        onChange={handleDropdownChange}
        onSearchChange={handleSearchChange}
        onSearchFocus={handleSearchFocus}
        isLoading={isLoading}
      />
      {hasDigits && (
        <div className="absolute -bottom-5 left-0 right-0 flex items-center justify-between px-1">
          <span className="text-xs text-slate-400">{digitCount}/11 znamenki</span>
          {digitCount === 11 && (
            <span className={`text-xs font-medium ${isValid ? "text-green-600" : "text-red-500"}`}>
              {isValid ? "Ispravan OIB" : "Neispravan OIB"}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(OibDropdown);
